import React, { useState, useEffect, useCallback, useRef } from 'react';

import { FlexView, Separator, Text, Row, Col, Button } from '@cuemath/comps';
import { trackEvent } from 'modules/analytics';
import { ACTIONS as notificationActions } from 'actions/notification';

import { useSelector } from 'react-redux';
import { store } from 'redux-rest-reducer';
import { Checkbox } from '@cuemath/web-comps';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';

const SPEAKERS = {
  spk_0: 'Speaker 0',
  spk_1: 'Speaker 1',
  spk_2: 'Speaker 2',
  spk_3: 'Speaker 3',
};

const prettyTimeFormat = seconds => {
  const hrs = ~~(seconds / 3600);
  const mins = ~~((seconds % 3600) / 60);
  const secs = ~~seconds % 60;

  let res = '';

  res += '' + hrs + ':' + (mins < 10 ? '0' : '');
  res += '' + mins + ':' + (secs < 10 ? '0' : '');
  res += '' + secs;

  return res;
};

const makeTranscriptFromRes = res => {
  const stats = {
    time: {},
    words: {},
    totalTime: 0,
  };
  const eventPayload = {};
  const subtitles = [];
  const uptakeInput = [];
  let currSpeaker = 'spk_0';
  let noOfSpeakers = 0;
  let currLine = '';
  let wordStartTime = 0;
  res.results.items.forEach((item, idx) => {
    if (item?.start_time) {
      if (Number(item.speaker_label.split('spk_')[1]) > noOfSpeakers) {
        noOfSpeakers += 1;
      }
      if (SPEAKERS[item.speaker_label] in stats.time) {
        stats.time[SPEAKERS[item.speaker_label]] += item.end_time - item.start_time;
        stats.words[SPEAKERS[item.speaker_label]] += 1;
        eventPayload[item.speaker_label + '_time'] += item.end_time - item.start_time;
        eventPayload[item.speaker_label + '_words'] += 1;
      } else {
        stats.time[SPEAKERS[item.speaker_label]] = item.end_time - item.start_time;
        stats.words[SPEAKERS[item.speaker_label]] = 1;

        eventPayload[item.speaker_label + '_time'] = item.end_time - item.start_time;
        eventPayload[item.speaker_label + '_words'] = 1;
      }
      stats.totalTime += item.end_time - item.start_time;
    }

    if (item.type === 'punctuation' && currLine.trim().length > 0) {
      currLine += item?.alternatives[0]?.content + ' ';
    } else if (item?.speaker_label !== currSpeaker && currLine.trim().length > 0) {
      subtitles.push({
        speakerLabel: SPEAKERS[currSpeaker],
        content: currLine,
        wordStartTime: prettyTimeFormat(wordStartTime),
      });
      uptakeInput.push({
        uid: idx,
        speaker: SPEAKERS[currSpeaker],
        text: currLine,
      });
      currSpeaker = item?.speaker_label;
      currLine = ' ' + item?.alternatives[0]?.content;
      wordStartTime = item?.start_time;
    } else {
      wordStartTime = wordStartTime === 0 ? item.start_time : wordStartTime;
      currLine += ' ' + item?.alternatives[0]?.content;
    }
  });

  if (currLine.trim().length) {
    subtitles.push({
      speakerLabel: SPEAKERS[currSpeaker],
      content: currLine,
      wordStartTime: prettyTimeFormat(wordStartTime),
    });
    uptakeInput.push({
      uid: subtitles.length,
      speaker: SPEAKERS[currSpeaker],
      text: currLine,
    });
  }

  return {
    stats,
    subtitles,
    eventPayload,
    uptakeInput,
    noOfSpeakers,
  };
};

const BUTTON_LABELS = ['Get Uptakes', 'Copy Uptakes'];

const Wrapper = styled(Row)`
  background-color: ${({ isUptake, isQuestion, isReasoning }) =>
    isUptake ? 'green' : isQuestion ? 'blue' : isReasoning ? 'yellow' : 'transparent'};
`;

const TranscriptModal = ({ signedUrl, teacherClassroomId }) => {
  const uptakeInputRef = useRef(undefined);
  const [transcript, setTranscript] = useState([]);
  const [uptakes, setUptakes] = useState([]);
  const [label, setLabel] = useState(BUTTON_LABELS[0]);

  const [isFetching, setIsFetching] = useState(false);
  const dispatch = useDispatch();

  const [uptakeSpeaker, setUptakeSpeaker] = useState(SPEAKERS['spk_0']);
  const [state, setState] = useState({
    time: {},
    words: {},
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const { data = {} } = useSelector(str => {
    return store.get(str.teacherClassLogs, teacherClassroomId) || {};
  });
  const { talk_data: talkMeterData = [] } = data || {};

  const {
    student_speech_time: studentSpeechTime = 0,
    teacher_speech_time: teacherSpeechTime = 0,
  } = talkMeterData.length ? talkMeterData[0] : {};

  const totalSpeechTime = Number(studentSpeechTime) + Number(teacherSpeechTime);

  const downloadTranscript = useCallback(() => {
    fetch(signedUrl)
      .then(response => response.json())
      .then(data => {
        if (data?.results?.items) {
          const {
            stats,
            subtitles,
            eventPayload,
            uptakeInput,
            noOfSpeakers,
          } = makeTranscriptFromRes(data);
          uptakeInputRef.current = uptakeInput;
          setTranscript(subtitles);
          setState({ ...stats, noOfSpeakers });
          setIsLoading(false);
          trackEvent(
            'TRANSCRIBE_STATS',
            {
              ...eventPayload,
              teacher_classroom_id: teacherClassroomId,
              ea_student_speech_time: Number(studentSpeechTime),
              ea_teacher_speech_time: Number(teacherSpeechTime),
            },
            { immediate: true },
          );
        }
      })
      .catch(err => setError('Something went wrong while downloading transcript'));
  }, [signedUrl, studentSpeechTime, teacherClassroomId, teacherSpeechTime]);

  const handleuptakeSpeakerChange = useCallback(value => {
    setUptakeSpeaker(value);
    setUptakes([]);
    setLabel(BUTTON_LABELS[0]);
  }, []);

  const showSnack = useCallback(
    msg =>
      dispatch({
        type: notificationActions.SHOW_NOTIFICATION,
        payload: {
          text: msg,
          duration: 10,
          showCloseIcon: true,
        },
      }),
    [dispatch],
  );

  const handleButtonClick = () => {
    if (label === BUTTON_LABELS[0]) {
      setIsFetching(true);
      const url =
        'https://jqdcknoc4ouc6re3r7gzmgaaxm0lxgfb.lambda-url.ap-southeast-1.on.aws';
      fetch(url, {
        method: 'POST',
        mode: 'cors',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          inputs: uptakeInputRef.current,
          parameters: {
            uptake_min_num_words: 5,
            uptake_speaker: uptakeSpeaker,
            filename: 'sample.csv',
          },
        }),
      })
        .then(response => {
          if (!response.ok) {
            showSnack('Something Went wrong');
          }
          return response.json();
        })
        .then(data => {
          setUptakes(data.utterances);
          setIsFetching(false);
          setLabel(BUTTON_LABELS[1]);
        })
        .catch(error => {});
    } else {
      navigator.clipboard.writeText(JSON.stringify(uptakes));
      showSnack('Copied');
    }
  };

  useEffect(() => {
    downloadTranscript();
  }, [downloadTranscript]);

  if (error.length) {
    return (
      <FlexView gutter={16} gap={16} justifyContent="center" alignItems="center">
        <Text weight="bold" size="large">
          error
        </Text>
      </FlexView>
    );
  }

  if (isLoading) {
    return (
      <FlexView gutter={16} gap={16} justifyContent="center" alignItems="center">
        <Text weight="bold" size="large">
          Loading ...
        </Text>
      </FlexView>
    );
  }

  return (
    <>
      <FlexView gutter={16} gap={16} justifyContent="center">
        <Text weight="bold" size="large">
          Transcript Stats:
        </Text>
        {Object.keys(state.time).map((key, idx) => (
          <Row gutter={4} gap={4} key={idx.toString()}>
            <Col>
              <Text weight="bold" size="large">
                {key}:
              </Text>
            </Col>
            <Col>
              <Text size="large">{(state.time[key] / 60).toFixed(2)} minutes</Text>
            </Col>
            <Col>
              <Text size="large">{state.words[key]} words</Text>
            </Col>
            <Col>
              <Text size="large">
                {((state.time[key] / state.totalTime) * 100).toFixed(2)} %
              </Text>
            </Col>
          </Row>
        ))}
        {studentSpeechTime > 0 && teacherSpeechTime > 0 && (
          <FlexView gutter={16}>
            <Text weight="bold" size="large">
              Talk Meter Stats:
            </Text>
            <Text weight="bold" size="large">
              Student: {(studentSpeechTime / 60).toFixed(2)} Minutes {'   '}
              {((studentSpeechTime / totalSpeechTime) * 100).toFixed(2)} %{'    '}
            </Text>
            <Text weight="bold" size="large">
              Teacher: {(teacherSpeechTime / 60).toFixed(2)} Minutes
              {'    '}
              {((teacherSpeechTime / totalSpeechTime) * 100).toFixed(2)} %
            </Text>
          </FlexView>
        )}
        <Separator height={20} transparent />
        <Row alignItems="center">
          {Object.keys(SPEAKERS).map((key, idx) => {
            if (idx > state?.noOfSpeakers) {
              return null;
            }
            return (
              <>
                <Col>
                  <Checkbox
                    value={SPEAKERS[key]}
                    checked={uptakeSpeaker === SPEAKERS[key]}
                    onChange={() => handleuptakeSpeakerChange(SPEAKERS[key])}
                  />
                </Col>
                <Col>
                  <Text size="large">{SPEAKERS[key]}</Text>
                </Col>
              </>
            );
          })}
          <Col>
            <Button
              label={label}
              onClick={handleButtonClick}
              busy={isFetching}
              disabled={isFetching}
            />
          </Col>
        </Row>
      </FlexView>
      <Separator height={20} transparent />
      {transcript.map((item, idx) => (
        <Wrapper
          gutter={16}
          gap={4}
          key={idx.toString()}
          isUptake={uptakes.length ? uptakes[idx].uptake === 1 : false}
          isReasoning={uptakes.length ? uptakes[idx].reasoning === 1 : false}
          isQuestion={uptakes.length ? uptakes[idx].question === 1 : false}
        >
          <Col>
            <Text size="large" key={idx.toString()} weight="bold">
              {item.speakerLabel} ({item.wordStartTime}):
            </Text>
          </Col>
          <Col auto>
            <Text size="large" key={idx.toString()}>
              {item.content}
            </Text>
          </Col>
        </Wrapper>
      ))}
    </>
  );
};

export default TranscriptModal;
